import { useState, useCallback } from 'react';
import { adminAPI } from '@/services/api';
import { MSAService } from '@/types';

export const useServiceActions = (onSuccess: () => void, services: MSAService[]) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Helper: ID로 서비스 찾아서 slug 추출
  const getServiceSlug = (serviceId: number): string | null => {
    const service = services.find(s => s.id === serviceId);
    return service?.service_slug || null;
  };

  const bulkAction = useCallback(async (
    serviceIds: number[],
    action: string,
    actionLabel: string,
    isPublic: boolean = false,
  ) => {
    const slugs = serviceIds.map(id => getServiceSlug(id)).filter(Boolean) as string[];
    if (slugs.length === 0) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('No services found');
    }

    setLoading(true);
    setError('');
    try {
      const result = await adminAPI.bulkServiceAction(slugs, action, isPublic);
      onSuccess();
      const successCount = result.success?.length || 0;
      const failedCount = result.failed?.length || 0;
      if (failedCount > 0) {
        return `${successCount}개 ${actionLabel} 완료, ${failedCount}개 실패`;
      }
      return `${successCount}개 서비스가 ${actionLabel}되었습니다.`;
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || `일괄 ${actionLabel}에 실패했습니다.`;
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const bulkApproveServices = useCallback(async (serviceIds: number[], isPublic: boolean) => {
    return bulkAction(serviceIds, 'approve', '승인', isPublic);
  }, [bulkAction]);

  const bulkSuspendServices = useCallback(async (serviceIds: number[]) => {
    return bulkAction(serviceIds, 'suspend', '일시중지');
  }, [bulkAction]);

  const bulkResumeServices = useCallback(async (serviceIds: number[]) => {
    return bulkAction(serviceIds, 'resume', '재개');
  }, [bulkAction]);

  const bulkDeleteServices = useCallback(async (serviceIds: number[]) => {
    return bulkAction(serviceIds, 'delete', '삭제');
  }, [bulkAction]);

  const bulkSetVisibility = useCallback(async (serviceIds: number[], isPublic: boolean) => {
    return bulkAction(serviceIds, isPublic ? 'set_public' : 'set_private', isPublic ? '공개 전환' : '비공개 전환');
  }, [bulkAction]);

  const approveService = useCallback(async (serviceId: number, isPublic: boolean) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.approveService(serviceSlug, isPublic);
      onSuccess();
      return '✅ 서비스가 승인되었습니다.';
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 승인에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const rejectService = useCallback(async (serviceId: number) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.rejectService(serviceSlug);
      onSuccess();
      return '서비스가 거부되었습니다.';
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 거부에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const suspendService = useCallback(async (serviceId: number) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.suspendService(serviceSlug);
      onSuccess();
      return '서비스가 일시 중지되었습니다.';
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 일시 중지에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const resumeService = useCallback(async (serviceId: number) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.resumeService(serviceSlug);
      onSuccess();
      return '서비스가 재개되었습니다.';
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 재개에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const deleteService = useCallback(async (serviceId: number) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.deleteService(serviceSlug);
      onSuccess();
      return '서비스가 삭제되었습니다.';
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 삭제에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const updateServiceVisibility = useCallback(async (serviceId: number, isPublic: boolean) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.toggleServiceVisibility(serviceSlug, isPublic);
      onSuccess();
      return `서비스가 ${isPublic ? '공개' : '비공개'}로 변경되었습니다.`;
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 공개 설정 변경에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const restoreService = useCallback(async (serviceId: number) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.restoreService(serviceSlug);
      onSuccess();
      return '✅ 서비스 삭제가 취소되었습니다. 서비스가 등록대기중 상태로 변경되었습니다.';
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 삭제 취소에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  const cancelRejection = useCallback(async (serviceId: number) => {
    const serviceSlug = getServiceSlug(serviceId);
    if (!serviceSlug) {
      setError('서비스를 찾을 수 없습니다.');
      throw new Error('Service not found');
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.cancelRejection(serviceSlug);
      onSuccess();
      return '✅ 서비스 거부가 취소되었습니다. 서비스가 등록대기중 상태로 변경되었습니다.';
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || '서비스 거부 취소에 실패했습니다.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [onSuccess, services]);

  return {
    loading,
    error,
    setError,
    bulkApproveServices,
    bulkSuspendServices,
    bulkResumeServices,
    bulkDeleteServices,
    bulkSetVisibility,
    approveService,
    rejectService,
    suspendService,
    resumeService,
    deleteService,
    updateServiceVisibility,
    restoreService,
    cancelRejection,
  };
};

